import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Award, TrendingUp, Camera, Clock, Star, CheckCircle2, DollarSign, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const GRADE_TONE = {
  A: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  B: "border-lime-500/40 text-lime-300 bg-lime-500/10",
  C: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  D: "border-rose-500/40 text-rose-300 bg-rose-500/10",
};

const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(0)}%`);
const money = (v) =>
  v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function VendorScorecardsPage() {
  const { properties } = useProperty();
  const [scope, setScope] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = scope !== "all" ? { property_id: scope } : {};
      const { data } = await api.get("/vendors/scorecards", { params });
      setItems(data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load"); }
    setLoading(false);
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader
        eyebrow="Procurement"
        title="Vendor Scorecards"
        subtitle="Composite score across resident ratings, bid accuracy, ETA hit rate, and photo evidence. Pick the sharpest bidder for your next RFP."
      />

      <div className="flex items-center gap-3 mb-4">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-56 bg-white/[0.04] border-white/10" data-testid="scorecard-scope">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            <SelectItem value="all">All communities</SelectItem>
            {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-zinc-500">{items.length} vendor{items.length === 1 ? "" : "s"}</div>
      </div>

      {items.length === 0 && !loading ? (
        <Card className="p-8 text-center border-white/10" data-testid="scorecard-empty">
          <Award className="h-8 w-8 mx-auto text-zinc-500 mb-2" />
          <div className="text-sm text-zinc-400">No vendors on file yet.</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((v) => (
            <Card key={v.vendor_id} className="p-4 border-white/10 bg-card" data-testid={`scorecard-${v.vendor_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-zinc-100 truncate">{v.vendor_name || "—"}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{v.vendor_email}</div>
                </div>
                <Badge variant="outline" className={GRADE_TONE[v.grade] || GRADE_TONE.D}
                       data-testid={`scorecard-grade-${v.vendor_id}`}>
                  {v.grade} · {v.score}
                </Badge>
              </div>

              <div className="grid grid-cols-4 gap-1 mb-3">
                <ComponentBar label="Rating" value={v.components.rating} max={40} icon={<Star className="h-3 w-3" />} />
                <ComponentBar label="Bid acc" value={v.components.bid_accuracy} max={25} icon={<TrendingUp className="h-3 w-3" />} />
                <ComponentBar label="ETA" value={v.components.eta} max={25} icon={<Clock className="h-3 w-3" />} />
                <ComponentBar label="Photos" value={v.components.photos} max={10} icon={<Camera className="h-3 w-3" />} />
              </div>

              {/* Job-flow stats — the new R83 block. Highlights completion, on-time,
                  avg cost and any SLA breaches so PMs can spot problem vendors instantly. */}
              <div className="grid grid-cols-2 gap-2 text-xs mb-3 pb-3 border-b border-white/5">
                <StatRow
                  icon={<CheckCircle2 className="h-3 w-3 text-emerald-400" />}
                  label="Completion" testid={`completion-${v.vendor_id}`}
                  value={v.completion_rate == null ? "—" : `${(v.completion_rate * 100).toFixed(0)}% (${v.n_jobs_resolved}/${v.n_jobs_total})`}
                />
                <StatRow
                  icon={<Clock className="h-3 w-3 text-sky-400" />}
                  label="On-time" testid={`ontime-${v.vendor_id}`}
                  value={v.on_time_pct == null ? "—" : `${v.on_time_pct.toFixed(0)}%`}
                />
                <StatRow
                  icon={<DollarSign className="h-3 w-3 text-amber-400" />}
                  label="Avg cost" testid={`avgcost-${v.vendor_id}`}
                  value={money(v.avg_cost)}
                />
                <StatRow
                  icon={<AlertTriangle className={"h-3 w-3 " + ((v.sla_breaches || 0) > 0 ? "text-red-400" : "text-zinc-500")} />}
                  label="SLA breach" testid={`slabreach-${v.vendor_id}`}
                  value={
                    <span className={(v.sla_breaches || 0) > 0 ? "text-red-300 font-semibold" : "text-zinc-300"}>
                      {v.sla_breaches ?? 0}
                    </span>
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <StatRow label="Reviews" value={`${v.avg_rating || "—"} (${v.n_reviews})`} />
                <StatRow label="Open jobs" value={v.n_jobs_open ?? 0} />
                <StatRow label="ETA hit" value={pct(v.eta_hit_rate)} />
                <StatRow label="Bid acc" value={v.bid_accuracy_pct != null ? `${v.bid_accuracy_pct}%` : "—"} />
                <StatRow label="Photos/job" value={v.photos_per_job} />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ComponentBar({ label, value, max, icon }) {
  const pct = Math.max(0, Math.min(1, (value || 0) / max));
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] text-zinc-500 mb-1">
        {icon}<span className="uppercase tracking-widest">{label}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/[0.03] overflow-hidden">
        <div className="h-full bg-orange-500" style={{ width: `${pct * 100}%` }} />
      </div>
      <div className="text-[11px] text-zinc-400 mt-0.5 tabular-nums">{value.toFixed(1)}/{max}</div>
    </div>
  );
}

function StatRow({ icon, label, value, testid }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 py-1" data-testid={testid}>
      <span className="text-[11px] text-zinc-500 uppercase tracking-widest flex items-center gap-1">
        {icon}{label}
      </span>
      <span className="text-zinc-100 tabular-nums">{value}</span>
    </div>
  );
}
